import type { ListQuery, Paginated, Target } from "./types";
import { API_MODE, applySearch, applySort, delay, paginate } from "./helpers";
import * as db from "@/lib/mocks/db";
import { request, mockRefusal, SavedNotShownError } from "./http";
import { paths, WIRE_UNSCANNABLE_REASONS, type WireTarget, type WirePage, type WireUnscannableReason } from "./wire";
import { readAllPages } from "./read-all";
import { isDefiniteRefusal, refusalReason } from "./write-outcome";

/**
 * The gateway's target and the console's are NOT the same object, and this is
 * where that stops being invisible. The wire carries `url`, `criticality`,
 * `exposure` and `tags`; the console's model also has a description, a type,
 * severity counts, a last-scan status and group membership, none of which the
 * platform stores or returns.
 *
 * 2.961 (the demo walk-through, 2026-10-06): severity counts were filled with
 * ZEROS here, and the Targets page showed "0 findings, 0 at risk, 0 scanned" for
 * every target whatever had run: absence asserted without asking (S-245's
 * class). A zero the platform did not send is not "what the platform knows".
 * They are now left unset, and no screen renders them.
 */
function targetFromWire(w: WireTarget): Target {
  return {
    id: w.id,
    projectId: w.project_id,
    address: w.url,
    // Carried as the platform sent it: no fallback to a level it never chose.
    criticality: w.criticality as Target["criticality"],
    exposure: w.exposure as Target["exposure"],
    // The platform stores no name; the first tag is the closest thing to one,
    // and the console uses it as the target's display name (targetName below).
    tags: w.tags,
    type: "web",
    // Neither is stored by the platform. `defaultScanProfileId` cannot be: scan
    // profiles have no table yet (S-166). Empty rather than a plausible default,
    // so the gap reads as a gap.
    defaultScanProfileId: "",
    continuousScanning: false,
    archivedAt: w.archived_at,
    archivedBy: w.archived_by,
    // 2.849 (ADR-115): omitted when the platform reported none.
    ...unscannableReasonFromWire(w.unscannable_reason),
    liveAuthProfileCount: liveAuthProfileCountOf(w.live_auth_profile_count),
  };
}

/**
 * 2.793: `live_auth_profile_count`, parsed strictly. A non-negative whole number
 * is the count; anything else — absent (an older platform), a string, a
 * fraction, a negative, null — is NOT REPORTED, never 0, because a 0 the
 * platform did not say would tell a person their target scans signed out.
 */
export function liveAuthProfileCountOf(v: unknown): number | undefined {
  return typeof v === "number" && Number.isSafeInteger(v) && v >= 0 ? v : undefined;
}

/** Mock mode carries the count the platform would: the target's profiles not revoked. */
function withMockProfileCount(t: Target): Target {
  return {
    ...t,
    liveAuthProfileCount: t.archivedAt ? 0 : db.authProfiles.filter((p) => p.targetId === t.id && !p.revokedAt).length,
  };
}

/**
 * 2.849 (S-165, ADR-115): `unscannable_reason` off the wire, strictly
 * (invariant 9). Only a non-empty string is a reason; it is carried even when
 * this build does not know it, so a newer reason still blocks the launch.
 */
export function unscannableReasonFromWire(raw: unknown): Pick<Target, "unscannableReason"> {
  return typeof raw === "string" && raw.trim() !== "" ? { unscannableReason: raw } : {};
}

/**
 * SDD Appendix A *Target unscannable reason*, in words — what is wrong with
 * the saved address, and what a person can do about it. The address itself is
 * never part of the sentence (the platform's reason quotes none either).
 */
export const UNSCANNABLE_REASON_WORDS: Record<WireUnscannableReason, string> = {
  userinfo_not_allowed:
    "Its saved address carries a user name or password, which a scan never sends. Archive it and add the address again without them.",
  target_not_single_host:
    "Its saved address does not name exactly one host (it has none, or it is a wildcard), so no scan can start from it. Archive it and add the one host it should be.",
  target_never_scan:
    "Its saved address is in a range the platform never scans, so every launch against it is refused.",
};

const KNOWN_UNSCANNABLE = new Set<string>(WIRE_UNSCANNABLE_REASONS);

/** A reason in words; one this build does not know is named, never dropped. */
export function unscannableSentence(reason: string): string {
  return KNOWN_UNSCANNABLE.has(reason)
    ? UNSCANNABLE_REASON_WORDS[reason as WireUnscannableReason]
    : `The platform reports that this target cannot be scanned (“${reason}”).`;
}

/**
 * 2.849: why the console will not offer a launch against this target, or
 * `undefined` when it will. An archived target takes no scan (ADR-088; the
 * launch would be `409 CONFLICT`), and an unscannable one would be refused
 * at launch with its reason — both said BEFORE the dialog, not after it.
 */
export function targetLaunchBlock(t: Pick<Target, "archivedAt" | "unscannableReason">): string | undefined {
  if (t.archivedAt) return "This target is archived, and an archived target takes no scan.";
  if (t.unscannableReason) return `This target cannot be scanned. ${unscannableSentence(t.unscannableReason)}`;
  return undefined;
}

/**
 * FR-14a, ADR-088 — archive a target: IRREVERSIBLE. The row is kept for
 * history; the target leaves the list, takes no new score or auth profile, and
 * its auth profiles are revoked (their credentials destroyed).
 */
export async function archiveTarget(projectId: string, id: string): Promise<Target> {
  if (API_MODE === "http") {
    return targetFromWire(await request<WireTarget>(paths.targetArchive(projectId, id), { method: "POST" }));
  }
  await delay();
  const t = db.targets.find((x) => x.id === id && x.projectId === projectId);
  if (!t) throw mockRefusal("NOT_FOUND", "target not found");
  t.archivedAt ??= new Date().toISOString();
  t.archivedBy ??= "user:mock";
  // A COPY, as a response would be: handing back the corpus object lets a
  // cache see the mutated row as the one it already holds, and never re-render.
  return { ...t };
}

export async function listTargets(q: ListQuery): Promise<Paginated<Target>> {
  if (API_MODE === "http") {
    // 2.752: cursor only. The contract's listTargets takes no `limit` and the
    // handler reads none: the server fixes the page size (invariant 10), so a
    // `limit` here was a parameter the platform silently ignored. `q.limit`
    // still bounds the MOCK page below.
    const page = await request<WirePage<WireTarget>>(paths.targets(q.projectId), {
      query: { cursor: q.cursor },
    });
    return { items: (page.items ?? []).map(targetFromWire), nextCursor: page.next_cursor };
  }
  await delay();
  let rows = db.targets.filter((t) => t.projectId === q.projectId && !t.archivedAt);
  rows = applySearch(rows, q, ["address", "description"]);
  rows = applySort(rows, q);
  const page = paginate(rows, q);
  return { ...page, items: page.items.map(withMockProfileCount) };
}

export async function getTarget(projectId: string, id: string): Promise<Target | undefined> {
  if (API_MODE === "http") {
    return targetFromWire(await request<WireTarget>(paths.target(projectId, id)));
  }
  await delay(120);
  const t = db.targets.find((x) => x.id === id && x.projectId === projectId);
  return t ? withMockProfileCount(t) : undefined;
}




/**
 * The platform stores no name for a target — TargetCreate is url, criticality,
 * exposure and tags (FR-14). So a "name" is carried as the FIRST tag, and read
 * back as one by `targetName`. `label` is that name; extra `tags` ride alongside.
 */
export interface NewTargetInput {
  address: string;
  label?: string;
  criticality?: Target["criticality"];
  exposure?: Target["exposure"];
  tags?: string[];
}

/** A target's display name: its first tag, else its address. The platform has
 *  no name field, so the tag a target was created with is the only one. */
export function targetName(t: { tags?: string[]; address: string }): string {
  return t.tags?.[0]?.trim() || t.address;
}

/**
 * 2.742 (S-692): a batch of targets that stopped part-way.
 *
 * Targets are created one request at a time, so a failure in the middle of a
 * list leaves the earlier ones CREATED. This carries what is known, so the page
 * can say it and not "may already have been added":
 *
 * - `added`: the addresses the platform confirmed (its 201), in order;
 * - `stoppedAt` and `cause`: the address that stopped the batch and why. With
 *   `stage: "create"` its POST threw (a refusal, or no answer: whether it was
 *   added is then not known); with `stage: "read-back"` its POST was confirmed,
 *   so it is also in `added`, and only reading it back failed;
 * - `notSent`: the addresses after it, never sent.
 */
export class TargetBatchError extends Error {
  constructor(
    readonly added: string[],
    readonly stoppedAt: string,
    readonly stage: "create" | "read-back",
    readonly cause: unknown,
    readonly notSent: string[],
  ) {
    super(`target batch stopped at ${stoppedAt} (${stage}) after ${added.length} added`);
    this.name = "TargetBatchError";
  }
}

/**
 * What the page says about a batch that stopped (2.742): what was added, what
 * stopped it, and what was never sent — each only as far as it is known.
 */
export function batchStopSentence(e: TargetBatchError): string {
  const total = e.added.length + (e.stage === "create" ? 1 : 0) + e.notSent.length;
  const parts: string[] = [];
  parts.push(e.added.length
    ? `${e.added.length} of ${total} added: ${e.added.join(", ")}.`
    : `None of the ${total} was confirmed added.`);
  if (e.stage === "read-back") {
    parts.push(`${e.stoppedAt} was added, but the display could not be refreshed.`);
  } else if (isDefiniteRefusal(e.cause)) {
    parts.push(`The platform refused ${e.stoppedAt}: ${refusalReason(e.cause)}`);
  } else {
    parts.push(`There was no answer for ${e.stoppedAt}, so whether it was added is not known.`);
  }
  if (e.notSent.length) parts.push(`Not sent: ${e.notSent.join(", ")}.`);
  parts.push("The target list is being read again.");
  return parts.join(" ");
}

export async function createTargets(projectId: string, inputs: NewTargetInput[]): Promise<Target[]> {
  if (API_MODE === "http") {
    // One request per target: the endpoint creates ONE, and pretending
    // otherwise here would hide a partial failure behind a single call.
    const created: Target[] = [];
    const added: string[] = [];
    // 2.742: a single target's failure is its own error, as before; only a
    // batch that stops part-way needs to say what it did before the stop.
    const stop = (i: number, stage: "create" | "read-back", cause: unknown): never => {
      // 2.743: one target confirmed but not read back is saved, not unknown.
      if (inputs.length === 1) throw stage === "read-back" ? new SavedNotShownError("The new target", cause) : cause;
      throw new TargetBatchError(added, inputs[i].address, stage, cause, inputs.slice(i + 1).map((x) => x.address));
    };
    for (const [i, inp] of inputs.entries()) {
      // `criticality: "normal"` was sent here and the gateway REFUSES it — its
      // vocabulary is low/medium/high/critical (openapi.yaml TargetCreate). The
      // console had never created a target over HTTP, so the 400 went unseen.
      // The label becomes the FIRST tag, because the platform stores no name.
      const tags = [inp.label?.trim(), ...(inp.tags ?? [])].filter((t): t is string => Boolean(t));
      // FR-14 (2.624): the 201 flags the live targets already at the address.
      let answer: { id: string; duplicate_count: number };
      try {
        answer = await request<{ id: string; duplicate_count: number }>(paths.targets(projectId), {
          method: "POST",
          body: {
            url: inp.address,
            criticality: inp.criticality ?? "low",
            exposure: inp.exposure ?? "external",
            tags,
          },
        });
      } catch (e) {
        return stop(i, "create", e);
      }
      added.push(inp.address);
      let read: Target | undefined;
      try {
        read = await getTarget(projectId, answer.id);
      } catch (e) {
        return stop(i, "read-back", e);
      }
      created.push({ ...(read as Target), duplicateCount: answer.duplicate_count });
    }
    return created;
  }
  await delay();
  const live = (address: string) =>
    db.targets.filter((t) => t.projectId === projectId && !t.archivedAt && t.address.toLowerCase() === address.toLowerCase()).length;
  const created: Target[] = inputs.map((inp, i) => ({
    id: `tgt-${Date.now()}-${i}`, projectId, address: inp.address, duplicateCount: live(inp.address),
    tags: [inp.label?.trim(), ...(inp.tags ?? [])].filter((t): t is string => Boolean(t)),
    type: "web", criticality: inp.criticality ?? "low", vulnerabilities: { critical: 0, high: 0, medium: 0, low: 0, informational: 0 },
    defaultScanProfileId: db.publishedScanProfileIds[0], continuousScanning: false,
  }));
  db.targets.push(...created);
  return created;
}

/**
 * 2.668 — every target in a project, for a caller that treats the list as
 * complete: the dashboard's Targets count and the launch page's picker.
 *
 * GET /v1/projects/{id}/targets pages at 100 (`targets.ListPageSize`) and
 * declares ONLY a cursor. The dashboard counted the first page, and the picker
 * showed the first page and sent `search` and `limit`, neither of which the
 * route takes, so its filter box did nothing over HTTP.
 * It wraps `listTargets`, so it works in both modes.
 */
export async function listAllTargets(projectId: string): Promise<Target[]> {
  return readAllPages<Target>(async (cursor) => {
    const page = await listTargets({ projectId, cursor });
    return { items: page.items, next_cursor: page.nextCursor };
  }, "targets");
}

/** The picker's filter, applied here since the route has none: address, description, tags. */
export function filterTargets(rows: Target[], search: string): Target[] {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((t) =>
    [t.address, t.description ?? "", ...(t.tags ?? [])].some((v) => v.toLowerCase().includes(q)),
  );
}
